import axios from 'axios';

// Configure base API URL - change this to match your backend endpoint
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Store auth token in sessionStorage so each browser tab is independent
let authToken = sessionStorage.getItem('authToken');

// Attach Authorization header on every request when a token is present
axios.interceptors.request.use((config) => {
  if (authToken) {
    config.headers['Authorization'] = `Bearer ${authToken}`;
  }
  return config;
});

/**
 * Get random embeddings from the database
 * @param {number} count - Number of random embeddings to retrieve
 * @returns {Promise} A promise that resolves to the data
 */
export const getRandomEmbeddings = async (count = 5) => {
  try {
    const response = await axios.get(`${API_URL}/embeddings/random?count=${count}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching random embeddings:', error);
    throw error;
  }
};

/**
 * Get similar embeddings based on a reference embedding
 * @param {number} id - ID of the reference embedding
 * @param {number} count - Number of similar embeddings to retrieve
 * @returns {Promise} A promise that resolves to the data
 */
export const getSimilarEmbeddings = async (id, count = 10) => {
  try {
    const response = await axios.get(`${API_URL}/embeddings/similar/${id}?count=${count}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching similar embeddings:', error);
    throw error;
  }
};

/**
 * Get a single embedding by ID
 * @param {number} id - ID of the embedding to retrieve
 * @returns {Promise} A promise that resolves to the data
 */
export const getEmbeddingById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/embeddings/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching embedding:', error);
    throw error;
  }
};

/**
 * Search the database using full-text search
 * @param {string} query - Search query
 * @returns {Promise} A promise that resolves to the search results
 */
export const searchDatabase = async (query) => {
  try {
    const response = await axios.get(`${API_URL}/search?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    console.error('Error searching database:', error);
    throw error;
  }
};

// Login using username and password
export const login = async (username, password) => {
  try {
    const response = await axios.post(`${API_URL}/login`, { username, password });
    authToken = response.data.token;
    sessionStorage.setItem('authToken', authToken);
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

// Logout and clear the session cookie
export const logout = async () => {
  try {
    await axios.post(`${API_URL}/logout`);
  } catch (error) {
    console.error('Error logging out:', error);
  } finally {
    authToken = null;
    sessionStorage.removeItem('authToken');
  }
};

// Check if current session is authenticated
export const checkAuth = async () => {
  if (!authToken) return false;
  try {
    await axios.get(`${API_URL}/auth-check`);
    return true;
  } catch {
    await logout();
    return false;
  }
};
